import { StandardEvents } from '@shopify/events';
class CartEssentials extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController(); this.cartPending = false; this.repairFrame = null;
    this.viewport = this.querySelector('[data-viewport]');
    this.track = this.querySelector('[data-track]');
    this.status = this.querySelector('[data-status]');
    this.retryButton = this.querySelector('[data-retry]');
    this.messages = JSON.parse(this.querySelector('[data-messages]').textContent);
    this.total = Number(this.dataset.total); this.pages = Number(this.dataset.pages); this.page = Number(this.dataset.page);
    this.ids = new Set([...this.track.children].map(n => n.dataset.productId));
    this.nextURL = this.dataset.next; this.busy = false; this.failed = false;
    this.generation = (this.generation || 0) + 1;
    const options = { signal: this.controller.signal };
    this.addEventListener('click', event => {
      const arrow = event.target.closest('[data-scroll]');
      if (arrow) this.move(arrow.dataset.scroll === 'next' ? 1 : -1);
      if (event.target.closest('[data-retry]')) { this.failed = false; this.retryButton.hidden = true; this.load(); }
    }, options);
    document.addEventListener(StandardEvents.cartLinesUpdate, event => this.cartUpdate(event), options);
    this.viewport.addEventListener('scroll', () => { this.controls(); this.maybeLoad(); }, { ...options, passive: true });
    this.viewport.addEventListener('keydown', event => {
      if (event.target !== this.viewport || !['ArrowLeft','ArrowRight'].includes(event.key)) return;
      event.preventDefault(); this.move((event.key === 'ArrowRight' ? 1 : -1) * (this.rtl() ? -1 : 1));
    }, options);
    window.addEventListener('resize', () => { this.controls(); this.maybeLoad(); }, { ...options, passive: true });
    this.querySelector('[data-navigation]').hidden = false;
    if (!Number.isSafeInteger(this.total) || this.total < 1 || !Number.isSafeInteger(this.pages) || this.pages < 1 || (!this.loadedFromStart && this.page !== 1) || !Number.isSafeInteger(this.page) || this.page < 1 || this.page > this.pages || (!this.nextURL && this.ids.size !== this.total) || this.ids.size !== this.track.children.length || [...this.ids].some(id => !id?.trim()) || this.ids.size > this.total) { this.fail(); return; }
    this.loadedFromStart = true; this.announce(); this.controls();
    this.knownNodes = [...this.track.children];
    this.mutations = new MutationObserver(() => this.checkRefresh());
    this.mutations.observe(this,{ childList:true, subtree:true, attributes:true, attributeFilter:['data-page','data-next','data-total','hidden'] });
  }
  disconnectedCallback() {
    this.mutations?.disconnect(); this.generation = (this.generation || 0) + 1; this.request?.abort(); this.controller?.abort(); this.controller = null; this.busy = false; this.removeAttribute('aria-busy');
  }
  checkRefresh() {
    if (!this.isConnected || this.repairFrame) return;
    const track = this.querySelector('[data-track]');
    if (!this.querySelector('[data-navigation]')?.hidden && track === this.track && this.querySelector('[data-viewport]') === this.viewport && this.querySelector('[data-status]') === this.status && this.querySelector('[data-retry]') === this.retryButton && Number(this.dataset.page) === this.page && this.dataset.next === this.nextURL && Number(this.dataset.total) === this.total && track.children.length === this.ids.size && this.knownNodes?.every(n => track.contains(n))) return;
    const generation = this.generation;
    this.repairFrame = requestAnimationFrame(() => {
      this.repairFrame = null;
      if (!this.isConnected || generation !== this.generation) return;
      const retainRange = this.loadedFromStart && this.querySelector('[data-track]') === this.track && Number(this.dataset.page) === this.page && this.track.children.length === this.ids.size && this.knownNodes.every(n => this.track.contains(n));
      this.disconnectedCallback(); this.loadedFromStart = retainRange; this.connectedCallback();
    });
  }
  cartUpdate(event) {
    this.request?.abort(); this.generation++; this.busy = false; this.removeAttribute('aria-busy'); this.cartPending = true;
    const generation = this.generation;
    Promise.resolve(event.promise).catch(() => {}).finally(() => requestAnimationFrame(() => {
      if (!this.isConnected || generation !== this.generation) return;
      this.cartPending = false; this.failed = false; this.retryButton.hidden = true; this.checkRefresh();
      if (!this.repairFrame && !this.failed) { this.announce(); this.controls(); }
    }));
  }
  rtl() { return getComputedStyle(this.viewport).direction === 'rtl'; }
  nearEnd() {
    const last = this.track.lastElementChild?.getBoundingClientRect(), view = this.viewport.getBoundingClientRect();
    return last && (this.rtl() ? last.left >= view.left - view.width : last.right <= view.right + view.width);
  }
  maybeLoad() { if (!this.failed && this.nextURL && this.nearEnd()) this.load(); }
  controls() {
    const atStart = Math.abs(this.viewport.scrollLeft) < 2;
    const atEnd = Math.abs(this.viewport.scrollLeft) + this.viewport.clientWidth >= this.viewport.scrollWidth - 2;
    this.querySelector('[data-scroll="previous"]').disabled = atStart;
    this.querySelector('[data-scroll="next"]').disabled = atEnd && !this.nextURL;
  }
  async move(direction) {
    if (direction > 0 && this.nextURL && this.nearEnd()) await this.load();
    if (!this.isConnected) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    this.viewport.scrollBy({ left: direction * (this.rtl() ? -1 : 1) * this.viewport.clientWidth, behavior: reduced ? 'instant' : 'smooth' });
  }
  async load() {
    if (this.busy || this.failed || this.cartPending || !this.nextURL || !this.isConnected) return;
    this.busy = true; this.setAttribute('aria-busy','true'); this.status.textContent = this.messages.loading;
    const generation = this.generation, requestPath = window.location.pathname, request = new AbortController(); this.request = request;
    try {
      const url = new URL(this.nextURL, window.location.href);
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) throw new Error('Unexpected route');
      const current = new URL(window.location.href);
      for (const key of ['preview_theme_id','view']) if (!url.searchParams.has(key) && current.searchParams.has(key)) url.searchParams.set(key,current.searchParams.get(key));
      url.searchParams.set('section_id',this.dataset.sectionId);
      const response = await fetch(url, { credentials:'same-origin',signal:request.signal });
      if (!response.ok) throw new Error('Unavailable');
      const doc = new DOMParser().parseFromString(await response.text(),'text/html');
      const root = [...doc.querySelectorAll('s24-cart-essentials')].find(n => n.dataset.sectionId === this.dataset.sectionId && n.dataset.instance === this.dataset.instance && n.dataset.collectionId === this.dataset.collectionId);
      if (!root || Number(root.dataset.page) !== this.page + 1 || Number(root.dataset.pages) !== this.pages || Number(root.dataset.total) !== this.total) throw new Error('Changed collection');
      const nodes = [...root.querySelector('[data-track]').children], ids = new Set();
      if (!nodes.length) throw new Error('Empty page');
      for (const node of nodes) { const id = node.dataset.productId; if (!id?.trim() || this.ids.has(id) || ids.has(id)) throw new Error('Duplicate item'); ids.add(id); }
      if (this.ids.size + nodes.length > this.total) throw new Error('Count mismatch');
      const next = root.dataset.next || '';
      if ((!next && this.ids.size + nodes.length !== this.total) || (next && Number(root.dataset.page) >= this.pages)) throw new Error('Incomplete collection');
      if (!this.isConnected || generation !== this.generation || request.signal.aborted || requestPath !== window.location.pathname) return;
      this.track.append(...nodes);
      for (const id of ids) this.ids.add(id);
      this.knownNodes = [...this.track.children];
      this.page++; this.dataset.page = String(this.page); this.nextURL = next; this.dataset.next = next;
      this.announce(); this.controls();
    } catch {
      if (generation === this.generation && !request.signal.aborted && this.isConnected) this.fail();
    } finally {
      if (generation === this.generation) { this.busy = false; this.request = null; this.removeAttribute('aria-busy'); }
    }
  }
  announce() { this.status.textContent = (this.nextURL ? this.messages.progress : this.messages.complete).replace('__LOADED__',String(this.ids.size)).replace('__TOTAL__',String(this.total)); }
  fail() { this.failed = true; this.status.textContent = this.messages.error; this.retryButton.hidden = false; }
}
if (!customElements.get('s24-cart-essentials')) customElements.define('s24-cart-essentials',CartEssentials);
